import { useEffect } from 'react';
import { styled } from '@mui/material';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { Link, RouteComponentProps, navigate } from '@reach/router';
import { NetworkStatus, useLazyQuery } from '@apollo/client';
import { useAuth } from '@calefy-inc/authentication';
import { DisplayInlineError } from '../../common/ErrorHandling';
import type { AdminCompulifeApiCallRecordQuery } from '../../../gql/graphql';
import { graphql } from '../../../gql';
import { formatDateTimeForDisplay } from '../../../util';

const PAGE_SIZE = 20;

const callDetailPath = (callId: string) => {
  return `/insurtech/admin/policystream/compulifecalls/${callId}`;
};

interface CompulifeApiCallsAdminPageProps extends RouteComponentProps {}

export const CompulifeApiCallsAdminPage = (
  _props: CompulifeApiCallsAdminPageProps,
) => {
  const { token } = useAuth();
  const [runQuery, { data, loading, error, fetchMore, networkStatus }] =
    useLazyQuery(ADMIN_COMPULIFE_API_CALL_RECORD_PAGE, {
      notifyOnNetworkStatusChange: true,
    });

  useEffect(() => {
    if (token) {
      runQuery({
        variables: { token, first: PAGE_SIZE },
      });
    }
  }, [token, runQuery]);

  const connection = data?.compulifeApiCallRecord;
  const showBottomLoader =
    loading && (!connection || networkStatus === NetworkStatus.fetchMore);
  const rows =
    connection?.edges
      ?.map((edge) => edge?.node)
      .filter((node): node is NonNullable<typeof node> => Boolean(node)) ?? [];

  const loadMore = () => {
    if (!token || !connection?.pageInfo?.endCursor) {
      return;
    }
    fetchMore({
      variables: {
        token,
        first: PAGE_SIZE,
        after: connection.pageInfo.endCursor,
      },
      updateQuery: (
        previous: AdminCompulifeApiCallRecordQuery,
        { fetchMoreResult },
      ) => {
        if (!fetchMoreResult) {
          return previous;
        }
        const nextConn = fetchMoreResult.compulifeApiCallRecord;
        if (!nextConn) {
          return previous;
        }
        return {
          compulifeApiCallRecord: {
            ...nextConn,
            edges: [
              ...(previous.compulifeApiCallRecord?.edges ?? []),
              ...(nextConn.edges ?? []),
            ],
          },
        };
      },
    });
  };

  if (!token) {
    return <Typography>Sign in to view Compulife API calls.</Typography>;
  }

  return (
    <PageSection>
      <Typography variant='h5' component='h1' gutterBottom>
        Compulife API calls
      </Typography>
      <BackLink to='/insurtech/admin/policystream'>
        &larr; PolicyStream admin
      </BackLink>

      <DisplayInlineError error={error} />

      <ScrollTableContainer>
        <Table size='small' stickyHeader>
          <TableHead>
            <TableRow>
              {[
                'ID',
                'Created At',
                'Face Amount',
                'State',
                'Sex',
                'Smoker',
                'Health',
                'Response Status Code',
                'Compulife Language',
                'Mode Used',
                'Remote IP',
                'Spoofed IP',
                'Request URL',
              ].map((header) => {
                return <HeaderCell key={header}>{header}</HeaderCell>;
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              return (
                <ClickableRow
                  key={row.id}
                  onClick={() => navigate(callDetailPath(row.id))}
                >
                  <BodyCell>{row.id}</BodyCell>
                  <BodyCell>{formatDateTimeForDisplay(row.createdAt)}</BodyCell>
                  <BodyCell>{row.faceAmount}</BodyCell>
                  <BodyCell>{row.state}</BodyCell>
                  <BodyCell>{row.sex}</BodyCell>
                  <BodyCell>{row.smoker}</BodyCell>
                  <BodyCell>{row.health}</BodyCell>
                  <BodyCell>
                    {row.responseStatusCode !== null &&
                    row.responseStatusCode !== undefined
                      ? String(row.responseStatusCode)
                      : '—'}
                  </BodyCell>
                  <BodyCell>{row.compulifeLanguage}</BodyCell>
                  <BodyCell>{row.modeUsed}</BodyCell>
                  <BodyCell>{row.remoteIp ?? '—'}</BodyCell>
                  <BodyCell>{row.spoofedIp ?? '—'}</BodyCell>
                  <RequestUrlCell title={row.requestUrl}>
                    {row.requestUrl}
                  </RequestUrlCell>
                </ClickableRow>
              );
            })}
          </TableBody>
        </Table>
      </ScrollTableContainer>

      {connection?.pageInfo?.hasNextPage ? (
        <LoadMoreWrap>
          <Button
            type='button'
            variant='outlined'
            onClick={loadMore}
            disabled={loading && Boolean(connection)}
          >
            Load {PAGE_SIZE} more
          </Button>
        </LoadMoreWrap>
      ) : null}

      {showBottomLoader ? (
        <TableLoadingFooter>
          <CircularProgress size={24} />
          <LoadingHint>{connection ? 'Loading more…' : 'Loading…'}</LoadingHint>
        </TableLoadingFooter>
      ) : null}
    </PageSection>
  );
};

const PageSection = styled('section')(({ theme }) => {
  return { paddingTop: theme.spacing(2) };
});

const BackLink = styled(Link)(({ theme }) => {
  return {
    display: 'inline-block',
    marginBottom: theme.spacing(2),
  };
});

const ScrollTableContainer = styled(TableContainer)(({ theme }) => {
  return {
    maxWidth: '100%',
    overflowX: 'auto',
    marginTop: theme.spacing(2),
  };
});

const HeaderCell = styled(TableCell)(() => {
  return { fontWeight: 600, whiteSpace: 'nowrap' };
});

const BodyCell = styled(TableCell)(() => {
  return { whiteSpace: 'nowrap', verticalAlign: 'top' };
});

const RequestUrlCell = styled(TableCell)(({ theme }) => {
  return {
    verticalAlign: 'top',
    maxWidth: 360,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontFamily: 'monospace',
    fontSize: theme.typography.body2.fontSize,
  };
});

const ClickableRow = styled(TableRow)(({ theme }) => {
  return {
    cursor: 'pointer',
    '&:hover': { backgroundColor: theme.palette.action.hover },
  };
});

const LoadMoreWrap = styled('div')(({ theme }) => {
  return { marginTop: theme.spacing(2) };
});

const TableLoadingFooter = styled('div')(({ theme }) => {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  };
});

const LoadingHint = styled('span')(({ theme }) => {
  return {
    ...theme.typography.body2,
    color: theme.palette.text.secondary,
  };
});

const ADMIN_COMPULIFE_API_CALL_RECORD_PAGE = graphql(`
  query AdminCompulifeApiCallRecord(
    $token: String!
    $first: Int
    $after: String
  ) {
    compulifeApiCallRecord(token: $token, first: $first, after: $after) {
      edges {
        cursor
        node {
          id
          createdAt
          faceAmount
          state
          sex
          smoker
          health
          responseStatusCode
          compulifeLanguage
          modeUsed
          remoteIp
          spoofedIp
          requestUrl
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`);
